import { DEFAULT_ACCESSIBILITY_PREFERENCES } from './types';
import type {
  AccessibilityContentSpacing,
  AccessibilityContrast,
  AccessibilityPreferences,
  AccessibilityTextSize,
  AccessibilityTextSpacing,
} from './types';

const TEXT_SIZE_VALUES: AccessibilityTextSize[] = ['default', 'large', 'extra-large'];
const TEXT_SPACING_VALUES: AccessibilityTextSpacing[] = ['default', 'increased', 'maximum'];
const CONTENT_SPACING_VALUES: AccessibilityContentSpacing[] = ['default', 'comfortable', 'expanded'];
const CONTRAST_VALUES: AccessibilityContrast[] = ['default', 'high-contrast-light', 'high-contrast-dark'];

function storageKey(siteId: string): string {
  return `scoutingops:site:${siteId}:visitor-preferences:v1`;
}

function pickEnum<T extends string>(value: unknown, allowed: T[], fallback: T): T {
  return typeof value === 'string' && (allowed as string[]).includes(value) ? (value as T) : fallback;
}

function pickBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

// Validates every field independently against its known shape rather than
// rejecting the whole object — a value from an older/newer schema version
// or a corrupted entry shouldn't cost the visitor every other preference
// they set.
function sanitize(value: unknown): AccessibilityPreferences {
  const raw = (typeof value === 'object' && value !== null ? value : {}) as Record<string, unknown>;
  return {
    textSize: pickEnum(raw.textSize, TEXT_SIZE_VALUES, DEFAULT_ACCESSIBILITY_PREFERENCES.textSize),
    textSpacing: pickEnum(raw.textSpacing, TEXT_SPACING_VALUES, DEFAULT_ACCESSIBILITY_PREFERENCES.textSpacing),
    contentSpacing: pickEnum(raw.contentSpacing, CONTENT_SPACING_VALUES, DEFAULT_ACCESSIBILITY_PREFERENCES.contentSpacing),
    contrast: pickEnum(raw.contrast, CONTRAST_VALUES, DEFAULT_ACCESSIBILITY_PREFERENCES.contrast),
    readableFont: pickBoolean(raw.readableFont, DEFAULT_ACCESSIBILITY_PREFERENCES.readableFont),
    highlightLinks: pickBoolean(raw.highlightLinks, DEFAULT_ACCESSIBILITY_PREFERENCES.highlightLinks),
    enhancedFocus: pickBoolean(raw.enhancedFocus, DEFAULT_ACCESSIBILITY_PREFERENCES.enhancedFocus),
    reduceMotion: pickBoolean(raw.reduceMotion, DEFAULT_ACCESSIBILITY_PREFERENCES.reduceMotion),
    pauseAnimations: pickBoolean(raw.pauseAnimations, DEFAULT_ACCESSIBILITY_PREFERENCES.pauseAnimations),
  };
}

// localStorage can throw (Safari private mode, storage disabled, quota
// exceeded) — every access here fails safe back to defaults rather than
// breaking the published site for a visitor.
export const accessibilityPreferenceStorage = {
  load(siteId: string): AccessibilityPreferences {
    try {
      const raw = window.localStorage.getItem(storageKey(siteId));
      if (!raw) return DEFAULT_ACCESSIBILITY_PREFERENCES;
      return sanitize(JSON.parse(raw));
    } catch {
      return DEFAULT_ACCESSIBILITY_PREFERENCES;
    }
  },

  save(siteId: string, preferences: AccessibilityPreferences): void {
    try {
      window.localStorage.setItem(storageKey(siteId), JSON.stringify(preferences));
    } catch {
      // Storage unavailable — preferences simply won't persist this session.
    }
  },

  clear(siteId: string): void {
    try {
      window.localStorage.removeItem(storageKey(siteId));
    } catch {
      // Nothing to do if storage is unavailable.
    }
  },
};
